// src/components/admin/AdminStatsDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminStats } from '../services/api';
import {
    ResponsiveContainer,
    PieChart, Pie, Cell,
    BarChart, Bar, XAxis, YAxis, Tooltip, Legend
} from 'recharts';
import '../App.css';
import pointsIcon from '../assets/points_icon.png';

export default function AdminStatsDashboard() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('month'); // 'month' | 'season'
    const [searchInactive, setSearchInactive] = useState('');
    const [copied, setCopied] = useState(false);
    const [searchNick, setSearchNick] = useState('');

    const fetchStats = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getAdminStats('all');
            setData(res.data);
        } catch (err) {
            console.error('Ошибка загрузки статистики:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    if (loading || !data) {
        return <div className="loading-screen">Загрузка аналитики сервера...</div>;
    }

    const current = period === 'month' ? data.month : data.season;
    const activeCount = Math.max(0, current.totalUsersCount - current.inactiveCount);

    // Данные для круговой диаграммы активности
    const pieData = [
        { name: 'Активные', value: activeCount, color: '#66F190' },
        { name: 'Неактивные', value: current.inactiveCount, color: '#F04747' }
    ];

    // Данные для сравнительного столбчатого графика
    const barData = [
        {
            name: 'Метрики',
            Сообщения: current.totalMessages,
            'Войс (часы)': Math.round(current.totalVoiceMinutes / 60),
            'Новички': current.newMembersCount,
        }
    ];

    // Фильтрация неактивных пользователей по поиску
    const filteredInactive = current.inactiveUsers.filter(u =>
        u.name.toLowerCase().includes(searchInactive.toLowerCase()) ||
        u.userId.includes(searchInactive)
    );

    const copyInactiveIds = () => {
        const ids = current.inactiveUsers.map(u => u.userId).join('\n');
        navigator.clipboard.writeText(ids);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const filteredInvalidNicks = (current.invalidNicknames || []).filter(u =>
        u.displayName.toLowerCase().includes(searchNick.toLowerCase()) ||
        u.userId.includes(searchNick) ||
        u.reason.toLowerCase().includes(searchNick.toLowerCase())
    );

    return (
        <div className="analytics-page">
            {/* Навигация и заголовок */}
            <div className="analytics-header">
                <button className="back-link-btn" onClick={() => navigate('/')}>
                    &larr; Назад на главную
                </button>
                <h1>📊 Аналитический дашборд</h1>
                <p className="subtitle">Статистика активности и вовлеченности сервера</p>

                {/* Переключатель периода */}
                <div className="period-toggle-group">
                    <button
                        className={`toggle-btn ${period === 'month' ? 'active' : ''}`}
                        onClick={() => setPeriod('month')}
                    >
                        Текущий месяц
                    </button>
                    <button
                        className={`toggle-btn ${period === 'season' ? 'active' : ''}`}
                        onClick={() => setPeriod('season')}
                    >
                        Сезон / Все время
                    </button>
                </div>
            </div>

            {/* KPI КАРТОЧКИ */}
            <div className="kpi-grid">
                <div className="kpi-card card">
                    <div className="kpi-icon">💬</div>
                    <div className="kpi-info">
                        <span className="kpi-label">Всего сообщений</span>
                        <span className="kpi-value">{current.totalMessages.toLocaleString('ru-RU')}</span>
                    </div>
                </div>

                <div className="kpi-card card">
                    <div className="kpi-icon">🎙️</div>
                    <div className="kpi-info">
                        <span className="kpi-label">Голосовая активность</span>
                        <span className="kpi-value">{current.totalVoiceFormatted}</span>
                    </div>
                </div>

                <div className="kpi-card card">
                    <div className="kpi-icon"><img src={pointsIcon} alt="сезонные очки" className="points-icon seasonal-icon" title="Сезонные очки" /></div>
                    <div className="kpi-info">
                        <span className="kpi-label">Начислено POINTS</span>
                        <span className="kpi-value points-green">+{current.seasonalPointsEarned.toLocaleString('ru-RU')}</span>
                    </div>
                </div>

                <div className="kpi-card card">
                    <div className="kpi-icon">👥</div>
                    <div className="kpi-info">
                        <span className="kpi-label">Новых участников</span>
                        <span className="kpi-value">+{current.newMembersCount}</span>
                    </div>
                </div>
            </div>

            {/* ЛИДЕРЫ ПЕРИОДА */}
            <div className="leaders-row">
                <div className="leader-card card gold-border">
                    <div className="leader-badge">⭐ Лидер по XP</div>
                    {current.topXpUser ? (
                        <div className="leader-details">
                            <span className="leader-name">{current.topXpUser.name}</span>
                            <span className="leader-score">{current.topXpUser.xp.toLocaleString('ru-RU')} XP</span>
                            <span className="leader-id">ID: {current.topXpUser.userId}</span>
                        </div>
                    ) : (
                        <span className="no-data">Нет данных</span>
                    )}
                </div>

                <div className="leader-card card blurple-border">
                    <div className="leader-badge">🎙️ Лидер по войсу</div>
                    {current.topVoiceUser ? (
                        <div className="leader-details">
                            <span className="leader-name">{current.topVoiceUser.name}</span>
                            <span className="leader-score">{current.topVoiceUser.formatted}</span>
                            <span className="leader-id">ID: {current.topVoiceUser.userId}</span>
                        </div>
                    ) : (
                        <span className="no-data">Нет данных</span>
                    )}
                </div>
            </div>

            {/* СЕКЦИЯ ГРАФИКОВ */}
            <div className="charts-grid">
                {/* Круговая диаграмма соотношения участников */}
                <div className="chart-card card">
                    <h3>Вовлеченность аудитории</h3>
                    <p className="chart-desc">
                        Активные пользователи против спящих (&lt; {current.inactiveThreshold} XP)
                    </p>
                    <div className="chart-container">
                        <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    innerRadius={65}
                                    outerRadius={95}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {pieData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#2b2d31', border: '1px solid #36373d', borderRadius: '8px', color: '#fff' }}
                                />
                                <Legend verticalAlign="bottom" height={36} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="chart-footer-stat">
                        Всего пользователей: <b>{current.totalUsersCount}</b> (Активных: <b>{activeCount}</b>, Неактивных: <b>{current.inactiveCount}</b>)
                    </div>
                </div>

                {/* Столбчатый график активности */}
                <div className="chart-card card">
                    <h3>Общая активность за период</h3>
                    <p className="chart-desc">Количественные объемы действий участников</p>
                    <div className="chart-container">
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={barData} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
                                <XAxis dataKey="name" stroke="#72767d" />
                                <YAxis stroke="#72767d" />
                                <Tooltip cursor={false}
                                    contentStyle={{ backgroundColor: '#2b2d31', border: '1px solid #36373d', borderRadius: '8px', color: '#fff' }}
                                />
                                <Legend verticalAlign="bottom" height={36} />
                                <Bar dataKey="Сообщения" fill="#5867F0" radius={[6, 6, 0, 0]} />
                                <Bar dataKey="Войс (часы)" fill="#66F190" radius={[6, 6, 0, 0]} />
                                <Bar dataKey="Новички" fill="#FFD700" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* ТАБЛИЦА НЕАКТИВНЫХ ПОЛЬЗОВАТЕЛЕЙ */}
            <div className="inactive-section card">
                <div className="inactive-header">
                    <div>
                        <h3>💤 Список неактивных участников (&lt; {current.inactiveThreshold} XP)</h3>
                        <p className="chart-desc">
                            Всего обнаружено: <b>{current.inactiveCount}</b> человек за выбранный период
                        </p>
                    </div>
                    <div className="inactive-actions">
                        <input
                            type="text"
                            placeholder="Поиск по имени или ID..."
                            value={searchInactive}
                            onChange={e => setSearchInactive(e.target.value)}
                            className="inactive-search-input"
                        />
                        <button className="copy-ids-btn" onClick={copyInactiveIds}>
                            {copied ? '✓ Скопировано!' : '📋 Скопировать ID'}
                        </button>
                    </div>
                </div>

                <div className="inactive-table-wrapper">
                    <table className="inactive-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Пользователь</th>
                                <th>Discord ID</th>
                                <th>XP за период</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredInactive.length > 0 ? (
                                filteredInactive.slice(0, 50).map((u, i) => (
                                    <tr key={u.userId}>
                                        <td>{i + 1}</td>
                                        <td className="user-cell"><b>{u.name}</b></td>
                                        <td className="id-cell">{u.userId}</td>
                                        <td className="xp-cell">{u.xp} XP</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="empty-cell">Пользователи не найдены</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    {filteredInactive.length > 50 && (
                        <p className="table-limit-notice">Показаны первые 50 из {filteredInactive.length} записей.</p>
                    )}
                </div>
                <div className="inactive-section card" style={{ marginTop: '20px' }}>
                    <div className="inactive-header">
                        <div>
                            <h3 style={{ color: '#F04747' }}>📛 Нарушения правил никнеймов</h3>
                            <p className="chart-desc">
                                Обнаружено участников: <b>{current.invalidNicknamesCount || 0}</b> чел.
                            </p>
                        </div>
                        <div className="inactive-actions">
                            <input
                                type="text"
                                placeholder="Поиск по нику, ID или причине..."
                                value={searchNick}
                                onChange={e => setSearchNick(e.target.value)}
                                className="inactive-search-input"
                            />
                        </div>
                    </div>

                    <div className="inactive-table-wrapper">
                        <table className="inactive-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Ник на сервере / Discord</th>
                                    <th>Discord ID</th>
                                    <th>Причина нарушения</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredInvalidNicks.length > 0 ? (
                                    filteredInvalidNicks.map((u, i) => (
                                        <tr key={u.userId}>
                                            <td>{i + 1}</td>
                                            <td className="user-cell">
                                                <b>{u.displayName}</b>
                                                {u.serverNickname && <small style={{ color: '#72767d', display: 'block' }}>логин: {u.username}</small>}
                                            </td>
                                            <td className="id-cell">{u.userId}</td>
                                            <td style={{ color: '#F04747', fontWeight: 600 }}>{u.reason}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="empty-cell" style={{ color: '#66F190' }}>
                                            Все никнеймы участников соответствуют правилам! 🎉
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}